/**
 * Servicio para manejar configuraciones globales del sistema
 */
import apiClient from './apiClient';
import { API_ENDPOINTS } from '@/config/api';

/**
 * Cache para configuraciones globales
 */
let configCache = {};
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Obtiene una configuración global por clave
 * @param {string} clave - Clave de la configuración
 * @returns {Promise<any>} Valor de la configuración
 */
export const getGlobalConfig = async (clave) => {
  try {
    // Verificar cache
    const now = Date.now();
    if (cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION) && configCache[clave]) {
      //console.log(`⚙️ Config cache hit for: ${clave}`);
      return configCache[clave];
    }

    //console.log(`⚙️ Fetching config from API: ${clave}`);
    
    const response = await apiClient.get(API_ENDPOINTS.CONFIGURACIONES.GLOBAL_BY_CLAVE(clave));
    
    if (response.data?.success) {
      const config = response.data.data;
      
      // Convertir el valor según el tipo
      let valor = config.valor;
      switch (config.tipo) {
        case 'integer':
          valor = parseInt(valor, 10);
          break;
        case 'boolean':
          valor = valor === 'true' || valor === true;
          break;
        case 'number':
          valor = parseFloat(valor);
          break;
        case 'json':
          try {
            valor = JSON.parse(valor);
          } catch (e) {
            console.warn(`Failed to parse JSON config ${clave}:`, valor);
          }
          break;
        default:
          // string - no conversion needed
          break;
      }

      // Actualizar cache
      configCache[clave] = valor;
      cacheTimestamp = now;

      //console.log(`⚙️ Config loaded: ${clave} = ${valor} (${config.tipo})`);
      return valor;
    }
    
    throw new Error(response.data?.message || 'Configuration not found');
  } catch (error) {
    console.error(`❌ Error fetching config ${clave}:`, error);
    
    // Valores por defecto para configuraciones críticas
    const defaults = {
      'trial_days_free_plan': 30,
      'trial_warning_days': 7,
      'trial_grace_days': 7,
      'trial_enabled': true
    };
    
    if (defaults.hasOwnProperty(clave)) {
      console.warn(`⚠️ Using default value for ${clave}: ${defaults[clave]}`);
      return defaults[clave];
    }
    
    throw error;
  }
};

/**
 * Obtiene múltiples configuraciones globales
 * @param {string[]} claves - Array de claves de configuración
 * @returns {Promise<Object>} Objeto con las configuraciones
 */
export const getMultipleGlobalConfigs = async (claves) => {
  try {
    const configs = {};
    
    // Intentar obtener todas las configuraciones
    await Promise.allSettled(
      claves.map(async (clave) => {
        try {
          configs[clave] = await getGlobalConfig(clave);
        } catch (error) {
          console.warn(`Failed to load config ${clave}:`, error.message);
        }
      })
    );
    
    return configs;
  } catch (error) {
    console.error('❌ Error fetching multiple configs:', error);
    throw error;
  }
};

/**
 * Limpia el cache de configuraciones
 */
export const clearConfigCache = () => {
  configCache = {};
  cacheTimestamp = null;
  //console.log('🗑️ Config cache cleared');
};

/**
 * Hook específico para configuraciones de trial
 */
export const getTrialConfig = async () => {
  const configs = await getMultipleGlobalConfigs([
    'trial_days_free_plan',
    'trial_warning_days', 
    'trial_grace_days',
    'trial_enabled'
  ]);
  
  return {
    trialDays: configs.trial_days_free_plan || 30,
    warningDays: configs.trial_warning_days || 7,
    graceDays: configs.trial_grace_days || 7,
    enabled: configs.trial_enabled !== false
  };
};

const configuracionGlobalService = {
  getGlobalConfig,
  getMultipleGlobalConfigs,
  clearConfigCache,
  getTrialConfig
};

export default configuracionGlobalService;